export const LESSON_LIBRARY_PATH_KEY = 'rafa:lesson-library-path'
const LESSON_LIBRARY_SCROLL_KEY_SUFFIX = ':scroll-position'

const REMEMBERED_PARAMS = new Set([
  'query',
  'page',
  'per',
  'per_page',
  'order',
  'collection',
  'broad_categories[]',
  'sports[]',
  'channels[]',
])

// The committed /videos URL is the library state. Keep only user-controlled
// search, filter, sort and pagination params; transient entry-point context
// (open_search, embedded, playlist_id, debug/search strategy) must not become
// the destination of the primary Lessons tab.
export function canonicalLessonLibraryPath(location) {
  if (!location || location.pathname !== '/videos') return null

  const source = new URLSearchParams(location.search || '')
  const remembered = new URLSearchParams()

  source.forEach((value, key) => {
    if (REMEMBERED_PARAMS.has(key) && value.trim() !== '') remembered.append(key, value)
  })
  remembered.sort()

  const query = remembered.toString()
  return `/videos${query ? `?${query}` : ''}`
}

export function rememberLessonLibraryPath(storage, location, storageKey = LESSON_LIBRARY_PATH_KEY) {
  const path = canonicalLessonLibraryPath(location)
  if (!path || !storage) return null

  try {
    storage.setItem(storageKey, path)
  } catch (_error) {
    return null
  }

  return path
}

// Search responses update the page and URL with Turbo Streams rather than a
// Drive visit. Turbo therefore still associates the rendered page with the URL
// from the last full visit, which prevents a later restoration visit from
// finding this snapshot. Keep its cache location aligned with the committed
// search URL so the Lessons tab can restore the page without re-fetching it.
export function syncLessonLibraryCacheLocation(turbo, location) {
  const view = turbo?.session?.view
  if (!view || !location) return false

  view.lastRenderedLocation = location
  return true
}

export function rememberLessonLibraryScroll(
  storage,
  location,
  position,
  storageKey = LESSON_LIBRARY_PATH_KEY
) {
  const path = canonicalLessonLibraryPath(location)
  const x = Number(position?.x)
  const y = Number(position?.y)
  if (!storage || !path || !Number.isFinite(x) || !Number.isFinite(y)) return null

  const saved = { path, x: Math.max(0, x), y: Math.max(0, y) }
  try {
    storage.setItem(`${storageKey}${LESSON_LIBRARY_SCROLL_KEY_SUFFIX}`, JSON.stringify(saved))
  } catch (_error) {
    return null
  }

  return saved
}

export function readLessonLibraryScroll(
  storage,
  location,
  storageKey = LESSON_LIBRARY_PATH_KEY
) {
  const path = canonicalLessonLibraryPath(location)
  if (!storage || !path) return null

  try {
    const saved = JSON.parse(
      storage.getItem(`${storageKey}${LESSON_LIBRARY_SCROLL_KEY_SUFFIX}`)
    )
    if (saved?.path !== path) return null

    const x = Number(saved.x)
    const y = Number(saved.y)
    if (!Number.isFinite(x) || !Number.isFinite(y)) return null

    return { x: Math.max(0, x), y: Math.max(0, y) }
  } catch (_error) {
    return null
  }
}

export function readLessonLibraryPath(storage, storageKey = LESSON_LIBRARY_PATH_KEY) {
  if (!storage) return null

  let stored
  try {
    stored = storage.getItem(storageKey)
  } catch (_error) {
    return null
  }
  if (!stored) return null

  try {
    const base = new URL('https://rafa.invalid')
    const url = new URL(stored, base)
    if (url.origin !== base.origin) return null

    return canonicalLessonLibraryPath(url)
  } catch (_error) {
    return null
  }
}
